import type { SessionResponse, SessionRoom } from '@dataroom/shared';

// The submit portal is the SELLER surface, so it operates on rooms the signed-in
// actor works on the seller side of: the bound SELLER (migration 0018) or a live
// seller's-team member, who works here with the seller's access (owner decision
// 2026-10-15). Resolved at runtime from GET /auth/session (see useSession). A
// seller is usually in one deal, but the portal supports several: the "Your data
// rooms" landing lists these, and each room-scoped view is entered by its id in
// the path (/rooms/:roomId/...).
export function sellerRooms(session: SessionResponse | undefined): SessionRoom[] {
  return session?.rooms.filter((r) => r.isSeller || r.isSellerTeam) ?? [];
}

/** True when the caller is on the room's seller's team but is not a seller.
 * Only the sellers decide things for the company, so a team member never votes
 * or answers a letter of intent; whether they may READ the LOIs depends on the
 * Legal role (see {@link isLegalTeamOnly}). */
export function isSellerTeamOnly(room: SessionRoom | undefined): boolean {
  return !!room && room.isSellerTeam && !room.isSeller;
}

/** A seller's-team member who carries the Legal role: may read the LOIs, their
 * documents and each one's final outcome, and nothing else (no votes, no
 * controls). A plain team member (no Legal) sees no LOIs at all. */
export function isLegalTeamOnly(room: SessionRoom | undefined): boolean {
  return isSellerTeamOnly(room) && !!room?.isLegalTeam;
}

/** What the caller is to a room's letters of intent: `seller` (votes),
 * `legal` (reads the outcome only), or `none` (a team member without Legal). */
export type LoiAccess = 'seller' | 'legal' | 'none';

export function loiAccess(room: SessionRoom | undefined): LoiAccess {
  if (room?.isSeller) return 'seller';
  return isLegalTeamOnly(room) ? 'legal' : 'none';
}
